import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { SellingPlans } from '../../types';

export default function Section3SellingPlans() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<SellingPlans>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.selling_plans) {
      setData(state.formData.selling_plans);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.selling_plans, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Save data when it changes
    if (state.intake && data.intake_id) {
      saveSection('selling_plans', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof SellingPlans, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Selling Plans</h2>
        
        <div className="space-y-6">
          {/* Purchasing in Another City */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Are you planning to purchase another property in a different city after this sale?
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="purchasing_another_city"
                  value="true"
                  checked={data.purchasing_another_city === true}
                  onChange={() => updateField('purchasing_another_city', true)}
                  className="mr-2"
                />
                Yes
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="purchasing_another_city"
                  value="false"
                  checked={data.purchasing_another_city === false}
                  onChange={() => updateField('purchasing_another_city', false)}
                  className="mr-2"
                />
                No
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="purchasing_another_city"
                  value="unsure"
                  checked={data.purchasing_another_city === undefined}
                  onChange={() => updateField('purchasing_another_city', undefined)}
                  className="mr-2"
                />
                Not sure yet
              </label>
            </div>
          </div>

          {/* Attorney */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Do you have a real estate attorney?
            </label>
            <input
              type="text"
              value={data.attorney || ''}
              onChange={(e) => updateField('attorney', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Attorney name and firm (if applicable)"
            />
          </div>

          {/* Preferred Closing Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred closing date
            </label>
            <input
              type="text"
              value={data.preferred_closing_date || ''}
              onChange={(e) => updateField('preferred_closing_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder='e.g., "Before August", "Between June and July", "Flexible", or a specific date'
            />
            <p className="text-sm text-gray-500 mt-1">
              This helps us understand your timeline. Feel free to describe a range or general timeframe — we can work with flexible dates.
            </p>
          </div>

          {/* Timeline Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Any timeline considerations or constraints?
            </label>
            <textarea
              value={(data as any).timeline_notes || ''}
              onChange={(e) => updateField('timeline_notes' as any, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={3}
              placeholder="e.g., Need to close before school starts, coordinating with another purchase, etc."
            />
          </div>

          {/* Relocation Plans */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              If moving, where are you planning to relocate?
            </label>
            <textarea
              value={(data as any).relocation_destination || ''}
              onChange={(e) => updateField('relocation_destination' as any, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={2}
              placeholder="City/State or general area (if known)"
            />
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Understanding your selling timeline and plans helps us coordinate the closing process 
            and provide better service. Don't worry if you don't have all the answers yet - 
            we can adjust the timeline as needed.
          </p>
        </div>
      </div>
    </div>
  );
}
